import { useEffect, useRef } from 'react'
import type { ThemeDefinition } from '../types'
import { stickerTitle, stickerImage, stickerFact, stickerSourceUrl, stickerValue } from '../data'
import { useI18n } from '../i18n/context'
import starDarkIcon from '../assets/icons/dark/star.png'
import starLightIcon from '../assets/icons/light/star.png'

interface Props {
  stickerNumber: number
  albumSize: number
  theme: ThemeDefinition
  currency: string
  onOpenExternalLink: (url: string) => void
  onClose: () => void
}

/**
 * Full-screen overlay shown immediately after a sticker is collected.
 * Displays the sticker image, name, value and a fun fact about the character/place/card.
 */
export function CollectedOverlay({
  stickerNumber,
  albumSize,
  theme,
  currency,
  onOpenExternalLink,
  onClose
}: Props): JSX.Element {
  const { t } = useI18n()
  const closeRef = useRef<HTMLButtonElement>(null)
  const isDarkMode = document.body.classList.contains('theme-dark')
  const factIcon = isDarkMode ? starDarkIcon : starLightIcon

  const title = stickerTitle(theme, stickerNumber)
  const imageUrl = stickerImage(theme, stickerNumber)
  const fact = stickerFact(theme, stickerNumber)
  const sourceUrl = stickerSourceUrl(theme, stickerNumber)
  const value = stickerValue(stickerNumber, albumSize)

  // Focus close button and allow Escape to dismiss
  useEffect(() => {
    closeRef.current?.focus()
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    // Auto-dismiss after 8 seconds
    const timer = setTimeout(onClose, 8000)
    return () => {
      document.removeEventListener('keydown', onKey)
      clearTimeout(timer)
    }
  }, [onClose])

  // Compute a rarity label based on sticker number
  function rarityTier(): { label: string; color: string } {
    const pct = (stickerNumber - 1) / albumSize
    if (pct < 0.05) return { label: '⭐⭐⭐⭐⭐ Legendary', color: '#f59e0b' }
    if (pct < 0.15) return { label: '⭐⭐⭐⭐ Epic', color: '#8b5cf6' }
    if (pct < 0.3) return { label: '⭐⭐⭐ Rare', color: '#3b82f6' }
    if (pct < 0.55) return { label: '⭐⭐ Uncommon', color: '#10b981' }
    return { label: '⭐ Common', color: '#6b7280' }
  }

  const rarity = rarityTier()

  return (
    <div
      className="collected-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t.stickerCollectedTitle}
      onClick={onClose}
    >
      <div className="collected-card" onClick={(e) => e.stopPropagation()}>
        {/* Burst animation background */}
        <div className="collected-burst" aria-hidden="true">
          {Array.from({ length: 12 }).map((_, i) => (
            <span key={i} className="burst-ray" style={{ '--ray-i': i } as React.CSSProperties} />
          ))}
        </div>

        <div className="collected-header">
          <span className="collected-title-eyebrow">{t.stickerCollectedTitle}</span>
          <span className="collected-sticker-num">#{stickerNumber}</span>
        </div>

        <div className="collected-image-area">
          {imageUrl ? (
            <img className="collected-image" src={imageUrl} alt={title} />
          ) : (
            <div className="collected-image-placeholder">
              <span className="collected-placeholder-num">{stickerNumber}</span>
            </div>
          )}
        </div>

        <div className="collected-info">
          <h2 className="collected-name">{title}</h2>

          <div className="collected-meta">
            <span className="collected-rarity" style={{ color: rarity.color }}>
              {rarity.label}
            </span>
            <span className="collected-value">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency,
                maximumFractionDigits: 0
              }).format(value)}
            </span>
          </div>

          {fact && (
            <div className="collected-fact">
              <img src={factIcon} alt="" aria-hidden="true" className="collected-fact-icon" />
              <p>{fact}</p>
            </div>
          )}

          {sourceUrl && (
            <button
              className="ghost-button"
              onClick={() => onOpenExternalLink(sourceUrl)}
              type="button"
            >
              Open external source
            </button>
          )}
        </div>

        <button
          ref={closeRef}
          className="primary-button collected-close"
          onClick={onClose}
          type="button"
        >
          {t.stickerCollectedClose}
        </button>

        <div className="collected-progress-bar" aria-hidden="true">
          <div className="collected-progress-fill" />
        </div>
      </div>
    </div>
  )
}
